// /src/hooks/useMultiHealthApis.js
import { useEffect, useMemo, useRef, useState } from "react";
import { getHealthApiBase } from "../constants/healthApi";

export default function useMultiHealthApis({
  endpoints,
  baseUrl = getHealthApiBase(),
  timeout = 10000,
  retries = 1,
}) {
  const [dataByKey, setDataByKey] = useState({});
  const [errorsByKey, setErrorsByKey] = useState({});
  const [loading, setLoading] = useState(true);
 
  const abortRef = useRef(null);
  const keys = useMemo(() => Object.keys(endpoints || {}), [endpoints]);

  useEffect(() => {
    if (!endpoints || keys.length === 0) {
      setLoading(false);
      return;
    }

    // Abort previous requests
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    setLoading(true);
    setDataByKey({});
    setErrorsByKey({});

    const delay = (ms) => new Promise((r) => setTimeout(r, ms));

    const fetchWithTimeout = async (url) => {
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const res = await fetch(url, { signal });
        if (!res.ok) {
          const error = new Error(`HTTP ${res.status}`);
          error.status = res.status;
          throw error;
        }
        return await res.json();
      } finally {
        clearTimeout(timeoutId);
      }
    };

    const shouldRetry = (err) => {
      if (err.name === "AbortError") return false;
      return !err.status || (err.status >= 500 && err.status <= 599);
    };

    const getWithRetry = async (path) => {
      let attempt = 0;
      while (true) {
        try {
          return await fetchWithTimeout(`${baseUrl}${path}`);
        } catch (err) {
          if (attempt < retries && shouldRetry(err)) {
            attempt += 1;
            await delay(300 * attempt);
            continue;
          }
          throw err;
        }
      }
    };

    (async () => {
      const results = await Promise.all(
        keys.map(async (key) => {
          try {
            const data = await getWithRetry(endpoints[key]);
            return { key, data, error: undefined };
          } catch (error) {
            return { key, data: undefined, error };
          }
        })
      );

      if (signal.aborted) return;

      const nextData = {};
      const nextErrors = {};

      results.forEach(({ key, data, error }) => {
        if (data !== undefined) nextData[key] = data;
        if (error) nextErrors[key] = error;
      });

      setDataByKey(nextData);
      setErrorsByKey(nextErrors);
      setLoading(false);
    })();

    return () => controller.abort();
  }, [baseUrl, timeout, retries, keys.join("|")]);

  return { dataByKey, loading, errorsByKey };
}
